import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { RecurringIssueItem, RecurringIssueListResponse } from '../../core/models/recurring-issue.models';
import { LocalDatePipe } from '../../shared/pipes/local-date.pipe';

@Component({
  selector: 'app-recurring-issues-page',
  standalone: true,
  imports: [LocalDatePipe],
  templateUrl: './recurring-issues-page.component.html',
  styleUrl: './recurring-issues-page.component.scss',
})
export class RecurringIssuesPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly data = signal<RecurringIssueListResponse | null>(null);
  readonly expanded = signal<Set<string>>(new Set());

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);
    this.data.set(null);
    this.expanded.set(new Set());

    this.analyticsService
      .getRecurringIssues({ ...this.dateRange.toQueryParams(), top: 30 })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (loadId === this.currentLoadId) this.loading.set(false);
        }),
      )
      .subscribe({
        next: (response) => {
          if (loadId !== this.currentLoadId) return;
          this.data.set(response);
        },
        error: (err: unknown) => {
          if (loadId !== this.currentLoadId) return;
          console.error('Recurring issues request failed', err);
          this.error.set(this.formatError(err, 'Recurring issues could not be loaded.'));
        },
      });
  }

  toggleExpanded(signature: string): void {
    const current = new Set(this.expanded());
    if (current.has(signature)) {
      current.delete(signature);
    } else {
      current.add(signature);
    }
    this.expanded.set(current);
  }

  isExpanded(signature: string): boolean {
    return this.expanded().has(signature);
  }

  severityClass(severity: string): string {
    const s = severity?.toLowerCase();
    if (s === 'high') return 'sev-high';
    if (s === 'medium') return 'sev-medium';
    return 'sev-low';
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') return 'Request timed out after 8s.';
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) return `Cannot reach backend${error.url ? ` @ ${error.url}` : ''}.`;
      return `${fallback} (${error.status}: ${error.message})`;
    }
    return fallback;
  }
}
